import { C } from "../lib/colors.js";
import { ui } from "../lib/telemetry/instrument.js";
import { Button } from "./ui/Primitives.jsx";
import LoadingLine from "./LoadingLine.jsx";

/**
 * The shared lane's TAIL — its error line and "Load more", and nothing else.
 *
 * There is no "SHARED WITH ME" section any more (collaborative-forecasts design):
 * a forecast shared with you is one of your forecasts, so the rows are merged
 * into WATCHLIST/RECENT by `mergeByReceivedAt` and rendered by the same row
 * components as everything else. What cannot be merged is the shared lane's own
 * PAGINATION — `GET /api/forecasts/shared` is a separate keyset-paginated
 * endpoint from the owner list — so this chrome survives its section and is
 * rendered at the end of RECENT, where the oldest-received rows sit and where
 * "load more" therefore means what it looks like.
 *
 * RENDER CONDITION IS `error || hasMore`, NOT row count (coordinator ruling, kept
 * verbatim in spirit): an absent affordance is indistinguishable from a broken
 * one. Silence is correct when the lane is fully drained and healthy; it is NOT
 * correct for a failed load, where a truncated list looks identical to "that's
 * everything". `loading` deliberately does not gate it — a first page renders
 * nothing until it resolves to a real page or a real error, never a claim made
 * before the answer is known.
 */
export function SharedLaneTail({ loading, hasMore, error, loadMore }) {
  if (!error && !hasMore) return null;
  return (
    <>
      {error && (
        // Task 2.5/M6 (frontend review) — the copy says "try again later"; it
        // must actually offer a retry, or behaviour and copy disagree (and
        // the copy is the honest one). canLoadMore() (useSharedForecasts.js)
        // reopens the gate on error:true, so this click reaches a real
        // fetch, not a no-op. Inert (not clickable) while a retry is already
        // in flight, same pattern as the "Load more" span below.
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "10px 4px",
            fontSize: 13,
            color: C.gray,
          }}
        >
          {loading ? (
            <LoadingLine>Trying again…</LoadingLine>
          ) : (
            <>
              Couldn&apos;t load forecasts shared with you.
              <Button
                variant="ghost"
                size="sm"
                onClick={loadMore}
                {...ui("forecast-manager.shared-retry")}
              >
                Try again
              </Button>
            </>
          )}
        </div>
      )}
      {hasMore && !error && (
        <div style={{ display: "flex", justifyContent: "center", padding: "8px 4px" }}>
          {loading ? (
            <LoadingLine>Loading more forecasts…</LoadingLine>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={loadMore}
              {...ui("forecast-manager.shared-load-more")}
            >
              Load more
            </Button>
          )}
        </div>
      )}
    </>
  );
}
